import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Fab,
  Paper,
  Collapse,
  Button,
  Snackbar,
  Alert,
  SwipeableDrawer,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { mdiChat, mdiHistory } from "@mdi/js";
import MdiIcon from '../mdi/MdiIcon';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import useDeleteDialog from '../dialog/DeleteDialog/useDeleteDialog';
import { useChat } from './useChat';
import { ChatThread, type ChatThreadHandle } from './ChatThread';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import { ChatHeader } from './ChatHeader';
import { ConversationList } from './ConversationList';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';

/** Panel widths, in px. Narrow by default so the page stays readable behind it. */
const PANEL_WIDTH = 420;
const PANEL_WIDTH_EXPANDED = 760;

/** Ties the launcher to the panel and the history toggle to its list. */
const PANEL_ID = 'chat-panel';
const HISTORY_ID = 'chat-history';

/**
 * Whether a keystroke is aimed at something the user is already typing into.
 *
 * `/` is a character in most inputs, and hijacking it there would make those
 * fields impossible to fill in. Anything that takes text is excluded, along
 * with anything marked `contenteditable`.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const {
    messages,
    isLoading,
    error,
    conversationId,
    conversations,
    historyHasMore,
    historyLoading,
    historySearch,
    loadMoreConversations,
    searchConversations,
    sendMessage,
    abort,
    setError,
    regenerate,
    editMessage,
    clearConversation,
    loadConversation,
    deleteConversation,
    downloadFile,
  } = useChat();

  const threadRef = useRef<ChatThreadHandle>(null);
  const inputRef = useRef<ChatInputHandle>(null);

  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  // Widening a panel that is already the full width of a phone does nothing, so
  // the control is only offered where there is room for it.
  const canExpand = useMediaQuery(theme.breakpoints.up('sm'));
  const panelWidth = expanded ? PANEL_WIDTH_EXPANDED : PANEL_WIDTH;
  const reducedMotion = usePrefersReducedMotion();

  // Move the caret to the composer once the panel exists, so keyboard and
  // screen-reader users land on the only control that starts a conversation.
  useEffect(() => {
    if (open) {
      // The drawer animates in; focusing immediately lands on an element that is
      // still being positioned.
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // `/` opens the chat, and focuses the composer whether or not it was already
  // open -- the point is to be able to type immediately.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      event.preventDefault();
      setOpen(true);
      // Wait for the panel to mount before reaching for the caret.
      setTimeout(() => inputRef.current?.focus(), 0);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Sending something means the user wants to watch it arrive, so the thread is
  // re-attached to the bottom before the response starts.
  const handleSend = async (content: string) => {
    threadRef.current?.follow();
    await sendMessage(content);
  };

  const handleNewChat = () => {
    clearConversation();
    setShowHistory(false);
  };

  const handleSelectConversation = (id: string) => {
    loadConversation(id);
    setShowHistory(false);
  };

  const handleDeleteConversation = (id: string) => {
    deleteConversation(id);
  };

  // The header's delete button deletes the active conversation, which is
  // irreversible — route it through the same confirmation dialog.
  const { openDialog } = useDeleteDialog();

  const handleDeleteActiveConversation = () => {
    if (!conversationId) return;
    openDialog(() => deleteConversation(conversationId));
  };

  // One panel body, rendered by whichever drawer suits the screen. Splitting it
  // out is what keeps the two variants from drifting apart.
  const panel = (
    <>
      <ChatHeader
        onNewChat={handleNewChat}
        onDelete={conversationId ? handleDeleteActiveConversation : undefined}
        showDelete={!!conversationId}
        expanded={expanded && canExpand}
        onToggleExpanded={canExpand ? () => setExpanded(!expanded) : undefined}
        onClose={() => setOpen(false)}
      />

      <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Button
          size="small"
          startIcon={<MdiIcon d={mdiHistory} />}
          onClick={() => setShowHistory(!showHistory)}
          aria-expanded={showHistory}
          aria-controls={HISTORY_ID}
        >
          {showHistory ? <Trans>Hide history</Trans> : <Trans>Show history</Trans>}
        </Button>
      </Box>

      <Collapse in={showHistory} timeout={reducedMotion ? 0 : undefined}>
        <Paper
          id={HISTORY_ID}
          variant="elevation"
          elevation={8}
          sx={{
            // Shaped like a snackbar: a floating, rounded, lifted card rather
            // than a strip flush with the panel edges. It sits over the
            // conversation rather than pushing it down, so the shape has to
            // say "this is a layer above the thread".
            m: 1,
            borderRadius: 2,
            // Tall enough for the search box, a page of threads and the load
            // more control, and scrolls so it cannot push the conversation
            // off the panel.
            maxHeight: 320,
            // The list inside does the scrolling, so the card itself is a
            // column that clips rather than a window onto the page.
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ConversationList
            conversations={conversations}
            currentId={conversationId}
            onSelect={handleSelectConversation}
            onDelete={handleDeleteConversation}
            search={historySearch}
            onSearch={searchConversations}
            hasMore={historyHasMore}
            loading={historyLoading}
            onLoadMore={loadMoreConversations}
          />
        </Paper>
      </Collapse>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          // Without this the flex child grows past the panel and the page
          // scrolls instead of the message list.
          minHeight: 0,
          p: 0,
          overflow: 'hidden',
        }}
      >
        <ChatThread
          ref={threadRef}
          messages={messages}
          isLoading={isLoading}
          onSendPrompt={handleSend}
          onEdit={editMessage}
          onRegenerate={regenerate}
          onDownloadFile={downloadFile}
        />

        {/* Input */}
        <ChatInput ref={inputRef} onSend={handleSend} onStop={abort} disabled={isLoading} />
      </Box>
    </>
  );

  const drawerProps = {
    hideBackdrop: true,
    disableScrollLock: true,
    disableEnforceFocus: true,
    slotProps: {
      root: {
        sx: {
          // The overlay root covers the whole viewport, so it would swallow
          // every click meant for the page behind. pointer-events is
          // inherited, so the panel has to opt back in below.
          pointerEvents: 'none',
        },
      },
      paper: {
        id: PANEL_ID,
        // The page is not inert behind this panel, so announcing it as a modal
        // dialog would misdescribe it to a screen reader.
        'aria-modal': false,
        sx: {
          pointerEvents: 'auto',
          width: { xs: '100%', sm: panelWidth },
          maxWidth: '100vw',
          height: '100%',
          // `100dvh` tracks the viewport as the mobile browser chrome and the
          // on-screen keyboard come and go; `100vh` does not, which left the
          // composer hidden behind the keyboard.
          maxHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
        },
      },
    },
  };

  return (
    <>
      {/* Floating Button */}
      <Fab
        color="primary"
        onClick={() => setOpen(true)}
        aria-label={t`Open AI chat`}
        // The launcher hides itself while the panel is open, so `aria-expanded`
        // is only ever read as false -- but `aria-controls` is what ties the
        // button to the region it reveals, and that holds in both states.
        aria-expanded={open}
        aria-controls={PANEL_ID}
        sx={{
          position: 'fixed',
          // Clear of the home indicator / gesture bar on phones and tablets.
          bottom: 'max(24px, env(safe-area-inset-bottom))',
          right: 'max(24px, env(safe-area-inset-right))',
          zIndex: 9999,
          // The panel is docked to the same edge, and sits below this z-index,
          // so the button would otherwise float on top of it. The panel has its
          // own close button, so hiding this while open loses nothing.
          display: open ? 'none' : 'flex',
        }}
      >
        <MdiIcon d={mdiChat} size={24} />
      </Fab>

      {/*
        A right-docked panel rather than a centred modal.

        The page behind stays live: no backdrop is rendered, the overlay root
        stops taking pointer events, the body is not scroll-locked, and focus is
        not trapped. That combination is what lets the user select text and
        click through to the rest of the app while the chat is open, so this is
        deliberately not a backdrop-dismiss modal any more.

      */}
      {isNarrow ? (
        // Below `sm` the panel covers the whole screen, so the only ways out are
        // the close button and this gesture. Discovery is off and the edge is
        // deliberately narrow: a transcript is scrolled vertically, and a
        // generous swipe area turns every attempt to read it into an attempt to
        // dismiss it. `SwipeableDrawer` also leaves the panel mounted when
        // closed, which the docked variant does not.
        <SwipeableDrawer
          anchor="right"
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          disableDiscovery
          swipeAreaWidth={28}
          hysteresis={60}
          {...drawerProps}
        >
          {panel}
        </SwipeableDrawer>
      ) : (
        <Drawer anchor="right" open={open} onClose={() => setOpen(false)} {...drawerProps}>
          {panel}
        </Drawer>
      )}


      {/* Errors surface here rather than inline, where they pushed the
          conversation around and scrolled themselves out of view. */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
